import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ChriResults, ChriRetrievalState, ChriStatus } from "interfaces/pages/components/ChriRecordProps";
import { useCustomModalStore, useUserStore } from "state";
import { useToastAlert } from "common/alerts";
import { useAudit } from "./useAudit";

const isGovcloud = (process.env.REACT_APP_GOVCLOUD === 'true');
const region = isGovcloud ? 'us-gov-west-1' : 'us-west-2';

export const useChriResults = (tcn: string, sid: string | undefined, userConfig: any) => {
    type MyParams = {
        registrationId: string;
    };

    const { registrationId } = useParams<keyof MyParams>() as MyParams;
    const [chriResults, setChriResults] = useState<ChriResults>();
    const [chriRetrievalState, setChriRetrievalState] = useState<ChriRetrievalState>(ChriRetrievalState.LOADING);
    const [chriRecordView, setChriRecordView] = useState('Original');
    const [editChriRedactedMode, setChriEditRedactedMode] = useState<boolean>(false);
    const [chriTextChanges, setChriTextChanges] = useState<boolean>(false);
    const [existChriRedaction, setExistChriRedaction] = useState<boolean>(false);
    const { addAuditEvent } = useAudit();

    const setOpenCustomModal = useCustomModalStore(state => state.setOpenCustomModal);
    const setCustomModalTitle = useCustomModalStore(state => state.setCustomModalTitle);
    const setContentText = useCustomModalStore(state => state.setContentText);
    const setContentStyle = useCustomModalStore(state => state.setContentStyle);
    const setModalActionsConfig = useCustomModalStore(state => state.setModalActionsConfig);

    const { notifySuccess, notifyError } = useToastAlert();

    const config = useUserStore(state => state.config);
    const useSynchronousRetrieval: boolean = config?.pages?.['applicant-details']?.chri?.useSynchronousRetrieval ?? false;

    const baseChriUrl = `https://chri.${region}.${process.env.REACT_APP_DOMAIN_NAME}`;
    const retrieveChriUrl: string = `${process.env.NODE_ENV === "production" ? process.env.REACT_APP_BASE_CHRI_RETRIEVE_URL ?? baseChriUrl : ""}${process.env.REACT_APP_BASE_CHRI_RETRIEVE_URL ?`/cch/${registrationId}` : '/chri/retrieve'}`;
    const getChriByRegId: string = `${process.env.NODE_ENV === "production" ? baseChriUrl : ""}/chri/${registrationId}`;
    const saveChriRedactedRapsheet: string = `${process.env.NODE_ENV === "production" ? baseChriUrl : ""}/chri/redactedRapsheet`;

    // On mount, check if a CHRI record already exists in the DB
    useEffect(() => {
        fetchExistingChriRecord();
    }, []);

    useEffect(() => {
        if (chriResults?.rapsheet_redacted?.trim()) {
            setExistChriRedaction(true);
        }
    }, [chriResults]);

    /**
     * Fetches existing CHRI record from the DB (GET /chri/{registrationId}).
     * Sets state to SUCCESS, FAIL, or INITIAL based on what's stored.
     * If legacy polling mode and status is PENDING, polls until resolved.
     */
    const fetchExistingChriRecord = async () => {
        try {
            const data: any = await (await fetch(getChriByRegId, {
                method: "GET",
                credentials: "include"
            })).json();

            const chri: ChriResults | undefined = data?.chriResults;

            if (chri) {
                if (chri.status === ChriStatus.PENDING && !useSynchronousRetrieval) {
                    // Legacy mode: poll until resolved
                    setTimeout(() => fetchExistingChriRecord(), 10000);
                } else if (chri.status === ChriStatus.SUCCESS) {
                    setChriResults(chri);
                    setChriRetrievalState(ChriRetrievalState.SUCCESS);

                    if (chri.rapsheet_redacted?.trim()) {
                        setChriRecordView('Original & Redacted');
                    }
                } else if (chri.status === ChriStatus.FAILED) {
                    setChriRetrievalState(ChriRetrievalState.FAIL);
                } else {
                    setChriRetrievalState(ChriRetrievalState.INITIAL);
                }
            } else {
                setChriRetrievalState(ChriRetrievalState.INITIAL);
            }
        } catch (e: unknown) {
            console.error('Failed to fetch existing CHRI record:', e);
            setChriRetrievalState(ChriRetrievalState.INITIAL);
        }
    };

    /**
     * Synchronous mode: calls POST /chri/retrieve which retrieves from LINK
     * and writes SUCCESS or FAILED to the DB immediately. No polling needed.
     */
    const retrieveChriSync = async (): Promise<boolean> => {
        const body = {
            sid,
            registration_id: registrationId,
            tcn,
            user: userConfig.userFamilyName + (userConfig.userGivenName || ""),
            tot: "ACK"
        };

        try {
            const resp = await (await fetch(retrieveChriUrl, {
                method: "POST",
                credentials: "include",
                body: JSON.stringify(body)
            })).json();

            addAuditEvent("Retrieve CHRI", "Results / CHRI Record", new Date(), registrationId);

            if (resp?.status === ChriStatus.SUCCESS || resp?.chriResults?.status === ChriStatus.SUCCESS) {
                const chri = resp.chriResults ?? resp;
                setChriResults(chri);
                setChriRetrievalState(ChriRetrievalState.SUCCESS);

                if (chri.rapsheet_redacted?.trim()) {
                    setChriRecordView('Original & Redacted');
                }
                return true;
            } else {
                setChriRetrievalState(ChriRetrievalState.FAIL);
                notifyError('CHRI Retrieval Failed', 'The system was unable to retrieve the CHRI record. Please try again.');
                return false;
            }
        } catch (e) {
            console.error('Failed to retrieve CHRI:', e);
            setChriRetrievalState(ChriRetrievalState.FAIL);
            notifyError('CHRI Retrieval Error', 'An unexpected error occurred while retrieving the CHRI record.');
            return false;
        }
    };

    /**
     * Legacy mode: triggers the workflow and polls for the result.
     * On re-retrieval, waits 30s before polling to avoid reading stale data.
     */
    const retrieveChriWithPolling = async (isReRetrieval: boolean = false) => {
        const body = {
            sid,
            registration_id: registrationId,
            tcn,
            user: userConfig.userFamilyName + (userConfig.userGivenName || ""),
            tot: "ACK"
        };

        try {
            const resp = await (await fetch(retrieveChriUrl, {
                method: "POST",
                credentials: "include",
                body: JSON.stringify(body)
            })).json();

            addAuditEvent("Retrieve CHRI", "Results / CHRI Record", new Date(), registrationId);

            if (resp) {
                if (isReRetrieval) {
                    // Wait for workflow to overwrite the existing record before polling
                    setTimeout(() => pollForChriResult(), 30000);
                } else {
                    pollForChriResult();
                }
            } else {
                setChriRetrievalState(ChriRetrievalState.FAIL);
            }
        } catch (e) {
            console.error('Failed to start CHRI workflow:', e);
            setChriRetrievalState(ChriRetrievalState.FAIL);
        }
    };

    /**
     * Polls GET /chri/{registrationId} until status is SUCCESS or FAILED.
     */
    const pollForChriResult = async () => {
        try {
            const data: any = await (await fetch(getChriByRegId, {
                method: "GET",
                credentials: "include"
            })).json();

            const chri: ChriResults | undefined = data?.chriResults;

            if (chri) {
                if (chri.status === ChriStatus.PENDING) {
                    setTimeout(() => pollForChriResult(), 10000);
                } else if (chri.status === ChriStatus.SUCCESS) {
                    setChriResults(chri);
                    setChriRetrievalState(ChriRetrievalState.SUCCESS);

                    if (chri.rapsheet_redacted?.trim()) {
                        setChriRecordView('Original & Redacted');
                    }
                } else if (chri.status === ChriStatus.FAILED) {
                    setChriRetrievalState(ChriRetrievalState.FAIL);
                }
            } else {
                // Record not yet created, keep polling
                setTimeout(() => pollForChriResult(), 10000);
            }
        } catch (e) {
            console.error('Error polling for CHRI result:', e);
            setChriRetrievalState(ChriRetrievalState.FAIL);
        }
    };

    /**
     * Saves a redacted rapsheet to the DB.
     */
    const saveRapsheetRedaction = async (rapsheetRedacted: string) => {
        const body = {
            id: chriResults?.id,
            rapsheet_redacted: rapsheetRedacted
        };

        try {
            const resp = await fetch(saveChriRedactedRapsheet, {
                method: "POST",
                credentials: "include",
                body: JSON.stringify(body)
            });

            if (resp.ok) {
                if (chriResults?.rapsheet_redacted) {
                    addAuditEvent("Redacted - Edited", "Results / CHRI Record", new Date(), registrationId);
                } else {
                    addAuditEvent("Redacted - Created", "Results / CHRI Record", new Date(), registrationId);
                }

                const updatedChriResults: ChriResults = {
                    ...chriResults!,
                    rapsheet_redacted: rapsheetRedacted
                };
                setChriResults(updatedChriResults);
                notifySuccess('Success', 'CHRI redaction saved!');
            } else {
                throw new Error(`HTTP error: ${resp.status}`);
            }
        } catch (e) {
            console.error('Failed to save rapsheet redaction:', e);
            notifyError('CHRI Redaction Error', 'Please try contact your administration');
        }
    };

    /**
     * Handles the "Retrieve CHRI" button click.
     * Uses synchronous or polling mode based on useSynchronousRetrieval config.
     */
    const handleRetrieveChri = useCallback(() => {
        if (!sid) {
            setOpenCustomModal(true);
            setCustomModalTitle("No record found!");
            setContentText("No record found for the applicant.");
            setContentStyle({ display: "flex", flexDirection: "column", justifyContent: "space-evenly" });
            setModalActionsConfig([
                {
                    onClick: () => setOpenCustomModal(false),
                    variant: "contained",
                    buttonTitle: "OK"
                }
            ]);
            return;
        }

        const startRetrieval = (isReRetrieval: boolean = false) => {
            setChriRetrievalState(ChriRetrievalState.LOADING);
            if (useSynchronousRetrieval) {
                retrieveChriSync();
            } else {
                retrieveChriWithPolling(isReRetrieval);
            }
        };

        if (chriRetrievalState === ChriRetrievalState.INITIAL || chriRetrievalState === ChriRetrievalState.FAIL) {
            startRetrieval(false);
        } else if (chriRetrievalState === ChriRetrievalState.SUCCESS) {
            setOpenCustomModal(true);
            setCustomModalTitle("Confirm New CHRI Retrieval");
            setContentText("A CHRI record is already on file. Retrieving again will pull the latest CHRI and replace the current record, including any redactions you have applied. Are you sure you want to lose all your redactions + current copy?");
            setContentStyle({ display: "flex", flexDirection: "column", justifyContent: "space-evenly" });
            setModalActionsConfig([
                {
                    onClick: () => setOpenCustomModal(false),
                    variant: "outlined",
                    buttonTitle: "CANCEL AND RETURN"
                },
                {
                    onClick: () => {
                        setOpenCustomModal(false);
                        addAuditEvent("Retrieve CHRI again", "Results / CHRI Record", new Date(), registrationId);
                        startRetrieval(true);
                    },
                    variant: "contained",
                    buttonTitle: "RETRIEVE AND REPLACE"
                }
            ]);
        }
    }, [chriRetrievalState, sid, useSynchronousRetrieval]);

    return {
        chriResults,
        chriRetrievalState,
        chriRecordView,
        editChriRedactedMode,
        chriTextChanges,
        existChriRedaction,
        setChriResults,
        setChriRetrievalState,
        setChriRecordView,
        setChriEditRedactedMode,
        setChriTextChanges,
        handleRetrieveChri,
        saveRapsheetRedaction
    };
}